import { useEffect, useRef, useState, memo, useCallback } from "react";
import { getOffsetProc } from "../chessUtils/util";
import styles from "./Piece.module.css";
import { ANIMATION_DURATION, Z_OVER } from "./animations";
import React from "react";
import { COLOR_LETTERS, PIECE_LETTERS } from "../chessUtils/fen";

function easeInOutQuad(
  elapsed: number,
  initialValue: number,
  goal: number,
  duration: number
): number {
  if ((elapsed /= duration / 2) < 1) {
    return ((goal - initialValue) / 2) * elapsed * elapsed + initialValue;
  }
  return (
    (-(goal - initialValue) / 2) * (--elapsed * (elapsed - 2) - 1) +
    initialValue
  );
}

function rafAnimation(
  start: { x: number; y: number },
  goal: { x: number; y: number },
  piece: HTMLDivElement | null,
  animating: React.RefObject<boolean>,
  onAnimationEnd?: () => void
) {
  if (!piece) {
    return;
  }
  const startTime = performance.now();
  loop();

  function loop() {
    const time = performance.now() - startTime;

    if (!animating.current) {
      return;
    }

    if (time < ANIMATION_DURATION) {
      let x = easeInOutQuad(time, start.x, goal.x, ANIMATION_DURATION);
      let y = easeInOutQuad(time, start.y, goal.y, ANIMATION_DURATION);
      piece.style.transform = `translate(${x}px, ${y}px)`;
      requestAnimationFrame(loop);
    } else {
      piece.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      onAnimationEnd?.();
    }
  }
}

function getOffsetPx(
  key: string,
  playingWhite: boolean,
  squareSize: number
): { x: number; y: number } {
  const proc = getOffsetProc(key, playingWhite);
  return {
    x: (proc.x / 100) * squareSize,
    y: (proc.y / 100) * squareSize,
  };
}

interface PieceData {
  id: string;
  role: "king" | "queen" | "rook" | "bishop" | "knight" | "pawn";
  color: "white" | "black";
}

interface PieceProps {
  piece: PieceData;
  currentKey: string;
  pieceRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  canDrag: boolean;
  playingWhite: boolean;
  animate: boolean;
  zIndex: number;
  chessSet: string;
  onAnimationEnd?: () => void | null;
  boardSize: number;
}

function Piece({
  piece,
  currentKey,
  pieceRefs,
  canDrag,
  playingWhite,
  animate,
  zIndex,
  chessSet,
  onAnimationEnd = null,
  boardSize,
}: PieceProps) {
  const { id, role, color } = piece;
  const lastKey = useRef(currentKey);
  const animating = useRef(animate);
  const animRef = useRef<Animation | null>(null);
  const squareSize = boardSize / 8;

  const [offset, setOffset] = useState(getOffsetPx(currentKey, playingWhite, squareSize));
  useEffect(() => {
    if (lastKey.current != currentKey && animate) {
      const pieceEl = pieceRefs.current[id];
      if (!pieceEl) {
        return;
      }
      animating.current = true;
      const start = getOffsetPx(lastKey.current, playingWhite, squareSize);
      const goal = getOffsetPx(currentKey, playingWhite, squareSize);

      try {
        // Use pixels throughout to avoid sub-pixel rounding issues
        animRef.current = pieceEl.animate(
          [
            { transform: `translate(${start.x}px, ${start.y}px)` },
            { transform: `translate(${goal.x}px, ${goal.y}px)` },
          ],
          {
            duration: ANIMATION_DURATION,
            easing: "ease-in-out",
            fill: "forwards",
          }
        );

        animRef.current.onfinish = () => {
          pieceEl.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
          if (animRef.current) {
            animRef.current.cancel();
            animRef.current = null;
          }
          setOffset(goal);
          onAnimationEnd?.();
        };
      } catch (error) {
        // Fallback to RAF for browsers that don't support WAAPI
        rafAnimation(
          start,
          goal,
          pieceEl,
          animating,
          () => {
            setOffset(goal);
            onAnimationEnd?.();
          }
        );
      }
    } else {
      const goal = getOffsetPx(currentKey, playingWhite, squareSize);
      setOffset(goal);
    }
    lastKey.current = currentKey;

    // Cleanup: cancel animation when effect re-runs or component unmounts
    return () => {
      animating.current = false;
      if (animRef.current) {
        try {
          animRef.current.cancel();
        } catch (e) {
          // Animation may already be finished/cancelled
        }
        animRef.current = null;
      }
    };
  }, [currentKey, playingWhite, animate, squareSize]);

  useEffect(() => {
    if (!animate) {
      animating.current = false;
      if (animRef.current) {
        animRef.current.cancel();
      }
      const goal = getOffsetPx(currentKey, playingWhite, squareSize);
      const pieceEl = pieceRefs.current[id];
      if (pieceEl) {
        pieceEl.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      }
    }
  }, [animate, squareSize]);

  // Update position when boardSize changes (e.g., on resize)
  // Only update if not currently animating
  // useEffect(() => {
  //   const pieceEl = pieceRefs.current[id];
  //   if (pieceEl && !animating.current) {
  //     const goal = getOffsetProc(currentKey, playingWhite);
  //     // Use percentage-based transform so it scales with board size
  //     pieceEl.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
  //     setOffset(goal);
  //   }
  // }, [boardSize, currentKey, playingWhite, id]);

  const classes = [];
  const pieceCode = COLOR_LETTERS[color] + PIECE_LETTERS[role];
  classes.push(styles.piece);
  if (!canDrag) {
    classes.push(styles.opponent);
  }

  const pieceImageUrl = `/images/pieces/${chessSet}/${pieceCode}.svg`;

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      pieceRefs.current[id] = el;
    },
    [id]
  );

  return (
    <div
      key={piece.id}
      ref={setRef}
      style={{
        zIndex,
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        backgroundImage: `url(${pieceImageUrl})`,
      }}
      className={classes.join(" ")}
    ></div>
  );
}

export default memo(Piece);
